import {Controller} from "@hotwired/stimulus"
import _ from "lodash"
import {
    SlickRowSelectionModel,
    SlickDataView,
    SlickGrid,
    SlickResizer,
    SlickCellSelectionModel,
    SlickCellCopyManager,
} from "slickgrid"

// ── Currency cell formatting ────────────────────────────────────────────────
// Numeric report cells used to ship as `data-controller="currency"` spans, so
// Stimulus instantiated a controller per *visible* cell (and again on every
// scroll as SlickGrid recycles rows) — the report grid's main bottleneck.
//
// Instead tabulator_amount_span now emits a compact sentinel placeholder:
//   \u0001$ amount \u0001 number_scale \u0001 comma_style \u0001 precision \u0001
// which the SlickGrid column formatter below expands and formats *once per
// render*. The logic mirrors currency_controller.js exactly (scale / comma /
// precision resolution, parentheses for negatives, displayZeroAs). A regex
// swap means it also works when the placeholder sits inside a drill-down <a>
// or is followed by a function-icon span.
const CUR_RE = /\u0001\$([^\u0001]*)\u0001([^\u0001]*)\u0001([^\u0001]*)\u0001([^\u0001]*)\u0001/g
const CUR_SCALE_DIVISORS = {units: 1, thousands: 1000, lakhs: 100000, millions: 1000000, crores: 10000000}

// <meta> defaults are page-global and only change on a full reload, so read
// them once instead of per cell.
let _curMeta = null
function curMeta() {
    if (_curMeta) return _curMeta
    const m = (n) => document.querySelector(`meta[name="${n}"]`)?.content
    _curMeta = {
        mono: m('numericFontMonospace') === 'true',
        round: m('userRoundPreference') || 'units',
        comma: m('userCommaStyle') || 'indian',
        precision: m('userPrecision') || '',
        zeroAs: m('displayZeroAs') || ''
    }
    return _curMeta
}

const _intlCache = new Map()
function curIntl(locale, minFractionDigits) {
    const key = `${locale}|${minFractionDigits}`
    let f = _intlCache.get(key)
    if (!f) {
        f = new Intl.NumberFormat(locale, {minimumFractionDigits: minFractionDigits})
        _intlCache.set(key, f)
    }
    return f
}

const isBlankAttr = (v) => v == null || v === '' || v === 'null' || v === 'undefined'

// scaleOverride = the card's live data-report-scale (or null). Mirrors the
// resolution order in currency_controller.js.
// blackNegatives = report_template.other_options.black_negative_numbers — when
// true, negatives keep brackets but drop the red text colour.
function buildCurrencyHtml(amountStr, ns, cs, pr, scaleOverride, blackNegatives = false) {
    const meta = curMeta()
    const amt = parseFloat(amountStr)

    // scale: card override > per-cell number_scale > meta default
    let scale = scaleOverride
    if (isBlankAttr(scale)) scale = ns
    if (isBlankAttr(scale)) scale = meta.round

    // precision: an active scale override forces the meta/user precision
    let precStr = scaleOverride ? '' : pr
    if (isBlankAttr(precStr)) precStr = meta.precision
    const precision = (precStr === '') ? (scale === 'units' ? 0 : 2) : parseInt(precStr)

    const commaStyle = isBlankAttr(cs) ? meta.comma : cs
    const locale = commaStyle === 'western' ? 'en-US' : 'en-IN'

    let text
    if (amt === 0 || isNaN(amt)) {
        text = meta.zeroAs === 'zero' ? '0' : '-'
    } else {
        const divisor = CUR_SCALE_DIVISORS[scale] || 1
        const rounded = parseFloat((Math.abs(amt) / divisor).toFixed(precision))
        const formatted = curIntl(locale, precision).format(rounded)
        text = amt < 0 ? `(${formatted})` : formatted
    }

    let cls = 'currency'
    if (amt < 0 && !blackNegatives) cls += ' text-red-600'
    if (meta.mono) cls += ' jetbrains-mono'
    // Keep the data-currency-* attrs (but NOT data-controller) so the
    // selection-sum bar can still read per-cell formatting from the span.
    return `<span class="${cls}" data-currency-amount-value="${amountStr}" data-currency-number-scale-value="${ns}" data-currency-comma-style-value="${cs}" data-currency-precision-value="${pr}">${text}</span>`
}

// Expand any currency placeholder(s) inside a cell value. Non-currency values
// (plain text, links, blanks) are returned unchanged. `scaleOf` is a function
// so the card scale is read lazily — only for cells that actually need it.
function renderCellValue(value, scaleOf, blackNegatives = false) {
    if (typeof value !== 'string') return value == null ? '' : value
    if (value.indexOf('\u0001$') === -1) return value
    return value.replace(CUR_RE, (_m, amount, ns, cs, pr) => buildCurrencyHtml(amount, ns, cs, pr, scaleOf(), blackNegatives))
}

export default class extends Controller {
    static targets = ['element', 'hitems']

    static values = {
        headers: {type: String, default: ''},
        ogHeaders: {type: Array, default: []},
        groupNames: {type: Array, default: []},
        options: {type: Object, default: {}},
        containerName: {type: String, default: ''},
        frozenCount: {type: String, default: ''},
        fontSize: {type: String, default: 'small'},
        autoHeight: {type: Boolean, default: false},
        scrollBar: {type: Boolean, default: false},
        forceFit: {type: Boolean, default: false},
        blackNegatives: {type: Boolean, default: false},
        minHeight: {type: Number, default: 0}
    }

    connect() {
        this.scrollInterval = null;
        let isMobile = window.innerWidth <= 768
        let headers = window[this.headersValue]
        // Rows arrive as a JSON <script> tag (see _table_data.html.erb) instead
        // of a data-* attribute, to avoid HTML-attribute escaping bloat.
        const rowsScript = this.element.querySelector('script[type="application/json"][data-slick-grid-rows]')
        let data = JSON.parse(rowsScript ? rowsScript.textContent : '[]');
        let groupNames = this.groupNamesValue;
        let containerName = this.containerNameValue;
        let frozenCount = _.toNumber(this.frozenCountValue);
        if (isMobile) {
            frozenCount = -1
        }
        if (headers === undefined) {
            const scriptContent = this.element.getElementsByTagName('script')[0].innerHTML
            const script = document.createElement("script")
            script.text = scriptContent
            document.head.appendChild(script).parentNode.removeChild(script)
            headers = window[this.headersValue]
        }

        // The report card carries the live number scale (data-report-scale,
        // written by the scale dropdown). Currency cells format against it in
        // the formatters below, so a card re-render re-applies the new scale.
        const card = this.element.querySelector('.lz-report-card') || this.element
        this.card = card
        const scaleOf = () => {
            const s = card.getAttribute('data-report-scale')
            return (s && s !== '' && s !== 'default') ? s : null
        }
        // Template other_options.black_negative_numbers — brackets stay, drop red.
        const blackNegatives = this.blackNegativesValue === true ||
            card.getAttribute('data-black-negatives') === 'true'

        var Slickformatter = function (row, cell, value, columnDef, dataContext) {
            var headerId = headers[cell].id
            let style = dataContext[`${headerId}_html_style`] || ''
            if (dataContext.indent >= 1) {
                style += ` slick-table-bg-indent-${dataContext.indent}`
            }
            // if (dataContext.tree_link && dataContext.indent === 0 && !dataContext._collapsed) {
            //   style += ' slick-table-bg-indent-1'
            // }
            return {html: renderCellValue(value, scaleOf, blackNegatives), addClasses: style};
        }

        var TaskNameFormatter = function (row, cell, value, columnDef, dataContext) {
            if (value == null || dataContext === undefined) {
                return "";
            }
            value = renderCellValue(value, scaleOf, blackNegatives)
            let headerId = headers[cell].id
            let text = ''
            let spacer = ''
            if (dataContext["parent"] !== null) {
                spacer = "<span style='display:inline-block;height:1px;width:" + (15 * dataContext["indent"]) + "px'></span>";
            }
            let style = dataContext[`${headerId}_html_style`] || ''
            if (dataContext.tree_link) {
                if (dataContext._collapsed) {
                    text = spacer + " <span class='toggle cursor-pointer lz-caret-right-icon inline-block h-[10px] w-[14px]'></span>&nbsp;&nbsp;" + value;
                } else {
                    text = spacer + " <span class='cursor-pointer toggle lz-caret-down-icon inline-block h-[10px] w-[14px]'></span>&nbsp;&nbsp;" + value;
                    // if (dataContext.indent === 0) {
                    //   style += ' slick-table-bg-indent-1'
                    // }
                }
            } else if (dataContext["parent"] !== null) {
                text = spacer + "&nbsp;&nbsp;&nbsp;" + value;
            } else {
                text = spacer + value;
            }
            if (dataContext.indent >= 1) {
                style += ` slick-table-bg-indent-${dataContext.indent}`
            }
            return {html: text, addClasses: style}
        };

        for (var i = 0; i < headers.length; i++) {
            if (i === 0) {
                headers[i]['formatter'] = TaskNameFormatter
            } else {
                headers[i]['formatter'] = Slickformatter
            }
        }

        let dataView;
        let grid;
        let columns = headers
        this.slickHeaders = headers
        // Active sort order, outermost column first. Shift+click on a header
        // appends to this list (see grid.onSort below) so rows can be ordered
        // by Segment -> Date -> Value instead of only the last column clicked.
        let sortCols = [{field: "title", dir: 1}];
        let colGroups = _.compact(_.map(columns, 'columnGroup'))

        let rowHeight = 28
        let topPanelRowHeight = 32

        if (this.fontSizeValue === 'medium') {
            rowHeight = 32
            topPanelRowHeight = 36
        } else if (this.fontSizeValue === 'large') {
            rowHeight = 36
            topPanelRowHeight = 40
        }

        let forceFit = this.forceFitValue
        let scrollBar = this.scrollBarValue


        var options = {
            enableCellNavigation: true,
            enableTextSelectionOnCells: true,
            createPreHeaderPanel: true,
            showPreHeaderPanel: colGroups.length > 0,
            preHeaderPanelHeight: colGroups.length > 0 ? topPanelRowHeight : 0,
            topPanelHeight: topPanelRowHeight,
            headerRowHeight: topPanelRowHeight,
            rowHeight: rowHeight,
            enableColumnReorder: false,
            enableAutoSizeColumns: true,
            frozenColumn: frozenCount,
            forceFitColumns: forceFit, // Add this line
            autoHeight: false,
            // Scroll one row at a time on arrow-key navigation instead of
            // jumping a full page and dropping the active cell at the top.
            emulatePagingWhenScrolling: false,
            // Multi-column sort: plain click sorts by one column (and clears
            // the rest), shift+click adds a column to the existing sort,
            // cmd/ctrl+click drops one. The numbered badge next to each arrow
            // shows that column's position in the sort order.
            multiColumnSort: true,
            numberedMultiColumnSort: true,
            sortColNumberInSeparateSpan: true
        };

        let CreateAddlHeaderRow = this.CreateAddlHeaderRow


        options = Object.assign(options, this.optionsValue);

        var myFilterDe = function (item) {

            if (item.parent != null) {
                var parent = this.items.find((ele) => ele.id === item.parent);

                while (parent) {
                    if (parent._collapsed) {
                        return false;
                    }
                    parent = this.items.find((ele) => ele.id === parent.parent);
                }
            }

            return true;
        }

        dataView = new SlickDataView({inlineFilters: true});
        dataView.beginUpdate();
        dataView.setItems(data);
        dataView.setFilter(myFilterDe);
        dataView.endUpdate();
        grid = new SlickGrid(this.elementTarget, dataView, columns, options);
        const cellSelectionModel = new SlickCellSelectionModel();
        grid.setSelectionModel(cellSelectionModel);

        // Excel-like aggregation bar: when one or more cells are selected, show
        // Count / Sum / Average of the numeric values in the selection.
        cellSelectionModel.onSelectedRangesChanged.subscribe((e, ranges) => {
            this.updateSelectionStats(ranges, grid, dataView);
        });

        // Register copy manager to enable Ctrl+C / Cmd+C clipboard copy
        const copyManager = new SlickCellCopyManager();
        grid.registerPlugin(copyManager);

        copyManager.onCopyCells.subscribe((e, args) => {
            const ranges = args.ranges;
            if (!ranges || ranges.length === 0) return;

            const rows = [];
            for (let i = 0; i < ranges.length; i++) {
                const range = ranges[i];
                for (let r = range.fromRow; r <= range.toRow; r++) {
                    const cellValues = [];
                    for (let c = range.fromCell; c <= range.toCell; c++) {
                        const cellNode = grid.getCellNode(r, c);
                        const val = cellNode ? cellNode.textContent.trim() : '';
                        cellValues.push(val);
                    }
                    rows.push(cellValues.join('\t'));
                }
            }
            const text = rows.join('\n');
            navigator.clipboard.writeText(text).catch(err => console.error('Copy failed:', err));

            // Blink copied cells with indigo color animation
            const copiedCells = this.elementTarget.querySelectorAll('.slick-cell.copied');
            copiedCells.forEach(cell => {
                cell.classList.add('slick-cell-copy-flash');
                setTimeout(() => {
                    cell.classList.remove('slick-cell-copy-flash');
                    cell.classList.remove('copied');
                }, 600);
            });
        });

        let resizer = new SlickResizer({
                container: `.${containerName}`, // DOM element selector, can be an ID or a class name
                // optionally define some padding and dimensions
                rightPadding: 5,    // defaults to 0
                bottomPadding: 10,  // defaults to 20
                minHeight: this.minHeightValue || 150,     // defaults to 180
                minWidth: 250,      // defaults to 300
                // you can also add some max values (none by default)
                // maxHeight: 1000
                // maxWidth: 2000
            },
            // the 2nd argument is an object and is optional
            // you could pass fixed dimensions, you can pass both height/width or a single dimension (passing both would obviously disable the auto-resize completely)
            // for example if we pass only the height (as shown below), it will use a fixed height but will auto-resize only the width
            // { height: 300 }
        );

        grid.registerPlugin(resizer);

        function slickResize() {
            let totalRowCount = dataView.filteredItems.length;
            let colGroupCount = colGroups.length > 0 ? 1 : 0;
            let tHeight = topPanelRowHeight;
            if (forceFit) {
                tHeight = tHeight + 70
            }
            let maxHeight = 650
            let totalHeight = (rowHeight * totalRowCount) + tHeight + (tHeight * colGroupCount);
            if (scrollBar) {
                totalHeight += 20
            } else {
                totalHeight += 5
            }
            // Only cap for large grids — small tables should render at their
            // natural full height so the page can scroll them, not the grid.
            let finalHeight = totalRowCount > 200 ? Math.min(totalHeight, maxHeight) : totalHeight;
            resizer.resizeGrid(0, {height: finalHeight, minHeight: finalHeight});
        }


        // Row hover highlighting intentionally disabled — moving the mouse
        // over the grid no longer selects/colours the whole row.

        grid.onCellChange.subscribe(function (e, args) {
            dataView.updateItem(args.item.id, args.item);
        });

        grid.onActiveCellChanged.subscribe((e, args) => {
            const activeCellNode = grid.getCellNode(args.row, args.cell);
            if (activeCellNode) {
                const cellRect = activeCellNode.getBoundingClientRect();
                const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                const headerOffset = this.isSticky ? (this.headerLeft ? this.headerLeft.offsetHeight : 0) : 0;
                const targetTop = headerOffset + 20;
                const targetBottom = viewportHeight - 20;
                if (cellRect.top < targetTop) {
                    window.scrollBy({
                        top: cellRect.top - targetTop,
                        behavior: 'smooth'
                    });
                }
                else if (cellRect.bottom > targetBottom) {
                    window.scrollBy({
                        top: cellRect.bottom - targetBottom,
                        behavior: 'smooth'
                    });
                }
            }
        })

        var getData = function (url, item, target) {
            fetch(url, {
                method: 'GET', headers: {
                    "Content-Type": "application/json", 'Accept': 'application/json',
                }
            }).then((response) => response.json()).then(result => {
                dataView.beginUpdate();
                let len = dataView.getItemCount()
                for (var i = 0; i < result.data.length; i++) {
                    result.data[i]['indent'] = item.indent + 1
                    result.data[i]['parent'] = item.id
                    result.data[i]['id'] = `id_${len + i + 1}`
                    result.data[i]['report_section_name'] = item['report_section_name']
                }
                target.classList.remove('lz-loader-icon', 'h-[12px]', 'w-[12px]')
                target.classList.add('lz-caret-down-icon', 'h-[10px]', 'w-[14px]')
                let index = dataView.getIdxById(item.id)
                dataView.insertItems(index + 1, result.data)
                item._collapsed = false;
                item.hasChildren = true;
                dataView.updateItem(item.id, item);
                grid.updateRowCount();
                dataView.endUpdate();
                CreateAddlHeaderRow(grid, options, columns);
                slickResize()
                this.reattachStickyHeaders();
                this.handleScroll()

            })
        }.bind(this)

        let getSortedChildren = (array, parent) => {
            let children = array.filter(item => item.parent === parent.id).sort(comparer);
            let sortedChildren = [];
            for (let child of children) {
                sortedChildren.push(child);
                sortedChildren.push(...getSortedChildren(array, child));
            }
            return sortedChildren;
        };


        grid.onSort.subscribe((e, args) => {
            // If headers are sticky, unstick them before sorting
            if (this.isSticky) {
                this.unstickHeaders();
            }

            // With multiColumnSort on, SlickGrid hands us the whole sort order
            // in args.sortCols; fall back to the single-column shape so this
            // keeps working if the option is ever turned off.
            let clickedCols = args.sortCols || (args.sortCol ? [{sortCol: args.sortCol, sortAsc: args.sortAsc}] : []);
            let nextSortCols = clickedCols
                .filter(c => c.sortCol && c.sortCol.field)
                .map(c => ({field: c.sortCol.field, dir: c.sortAsc ? 1 : -1}));
            if (nextSortCols.length) {
                sortCols = nextSortCols;
            }
            dataView.beginUpdate();

            let pData = _(dataView.items)
                .groupBy('report_section_name')
                .mapValues(group => group.filter(item => !item.parent).sort(comparer))
                .mapValues(group => group.flatMap(root => [root, ...getSortedChildren(dataView.items, root)]))
                .toPairs()
                .sortBy(([key]) => groupNames.indexOf(key))
                .fromPairs()
                .values()
                .flatten()
                .value();

            dataView.setItems(pData);
            grid.updateRowCount();
            dataView.endUpdate();
            CreateAddlHeaderRow(grid, options, columns);

            // Reattach sticky headers after grid updates are complete
            // Use requestAnimationFrame instead of setTimeout for better timing
            requestAnimationFrame(() => {
                this.reattachStickyHeaders();
                // Check scroll position to update sticky headers if needed
                this.handleScroll();
            });
        });

        // Compare on each sorted column in turn: the first column decides, ties
        // fall through to the next one, and so on.
        var comparer = function (a, b) {
            for (let i = 0; i < sortCols.length; i++) {
                let {field, dir} = sortCols[i];
                let x = a[`${field}_sort_value`], y = b[`${field}_sort_value`];
                if (x !== y) {
                    return dir * (x > y ? 1 : -1);
                }
            }
            return 0;
        }

        var checkItemClick = function (args, target) {
            var item = dataView.getItem(args.row);
            if (item) {
                if (!item._collapsed) {
                    item._collapsed = true;
                    dataView.updateItem(item.id, item);
                    CreateAddlHeaderRow(grid, options, columns);
                    slickResize();
                    this.reattachStickyHeaders();
                    this.handleScroll()
                } else {
                    if (item.hasChildren) {
                        item._collapsed = false;
                        dataView.updateItem(item.id, item);
                        CreateAddlHeaderRow(grid, options, columns);
                        slickResize();
                        this.reattachStickyHeaders();
                        this.handleScroll()
                    } else {
                        if (target.classList.contains("lz-caret-right-icon")) {
                            target.classList.remove('lz-caret-right-icon', 'h-[10px]', 'w-[14px]')
                            target.classList.add('lz-loader-icon', 'h-[12px]', 'w-[12px]')
                            getData(item.tree_link, item, target)
                        }
                    }
                }
            }
        }.bind(this)

        grid.onClick.subscribe(function (e, args) {
            var target = e.getNativeEvent().target
            if (target.classList.contains("toggle")) {
                checkItemClick(args, target)
                e.stopImmediatePropagation();
            }
        });

        dataView.onRowCountChanged.subscribe(function (e, args) {
            grid.updateRowCount();
            grid.render();
        });

        dataView.onRowsChanged.subscribe(function (e, args) {
            grid.invalidateRows(args.rows);
            grid.render();
        });

        CreateAddlHeaderRow(grid, options, columns);

        grid.onColumnsResized.subscribe(function (e) {
            CreateAddlHeaderRow(grid, options, columns);
        });

        // ── Auto-fit column width on double-click ────────────────────────────
        // SlickGrid already fires onColumnsResizeDblClick when the border between
        // two headers is double-clicked, but its built-in autosizeColumn() only
        // computes a number into column.autoSize.widthPx and never applies it —
        // so we measure and apply ourselves. Measurement runs the real formatter
        // and then strips tags, because numeric cells hold a compact currency
        // placeholder rather than the text the user actually sees.
        // There is deliberately no upper bound on the fitted width — a
        // double-clicked column grows to whatever its longest value needs and
        // you scroll sideways to read it, exactly like Excel. The single
        // exception is frozen columns, capped below.
        const AUTOFIT_MIN = 60
        const AUTOFIT_FROZEN_SHARE = 0.6   // frozen block may claim at most this much of the grid width
        const AUTOFIT_CELL_PADDING = 16    // .slick-cell left+right padding + border
        const AUTOFIT_HEADER_PADDING = 28  // header padding + sort/menu icon room
        const AUTOFIT_MAX_ROWS = 10000     // keep the double-click instant on huge reports
        const AUTOFIT_EDGE_ZONE = 8        // px from a header's right edge that counts as "on the border"

        const measureCtx = document.createElement('canvas').getContext('2d')
        const plainText = (html) => {
            if (html == null) return ''
            if (typeof html !== 'string') return String(html)
            return html.replace(/<[^>]*>/g, '')
                .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim()
        }

        const autoFitColumn = (colIdx) => {
            const cols = grid.getColumns()
            const col = cols[colIdx]
            if (!col) return

            const sampleCell = this.elementTarget.querySelector('.slick-cell')
            const cs = sampleCell ? getComputedStyle(sampleCell) : null
            measureCtx.font = cs ? `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}` : '13px sans-serif'

            let widest = 0
            const rowCount = Math.min(dataView.getLength(), AUTOFIT_MAX_ROWS)
            for (let r = 0; r < rowCount; r++) {
                const item = dataView.getItem(r)
                if (!item) continue
                let out
                try {
                    out = col.formatter
                        ? col.formatter(r, colIdx, item[col.field], col, item, grid)
                        : item[col.field]
                } catch (err) {
                    // a single unformattable row must not abort the whole measurement
                    out = item[col.field]
                }
                const text = plainText(out && out.html !== undefined ? out.html : out)
                let w = measureCtx.measureText(text).width
                // the first column renders a tree indent + collapse caret that the
                // stripped text can't account for
                if (colIdx === 0) {
                    w += (item.indent || 0) * 15 + (item.tree_link !== undefined ? 22 : 0)
                }
                if (w > widest) widest = w
            }
            widest += AUTOFIT_CELL_PADDING

            // never clip the header label itself
            const nameEl = grid.getHeaderColumn(colIdx)?.querySelector('.slick-column-name')
            if (nameEl) widest = Math.max(widest, nameEl.scrollWidth + AUTOFIT_HEADER_PADDING)

            let width = Math.ceil(Math.max(widest, col.minWidth || AUTOFIT_MIN))
            if (col.maxWidth) width = Math.min(width, col.maxWidth)

            // Frozen columns are the one place an unbounded width does real
            // damage: SlickGrid drops the freeze entirely (validateColumnFreeze-
            // Width -> frozenColumn = -1) the moment the frozen block is wider
            // than the grid container, so a long value would silently unpin the
            // columns instead of widening them. Keep the frozen block inside its
            // share of the grid; every other column is free to grow.
            const frozenIdx = grid.getOptions().frozenColumn
            if (frozenIdx >= 0 && colIdx <= frozenIdx) {
                const containerWidth = this.elementTarget.clientWidth || 0
                if (containerWidth > 0) {
                    let siblingsWidth = 0
                    for (let i = 0; i <= frozenIdx && i < cols.length; i++) {
                        if (i !== colIdx && cols[i] && !cols[i].hidden) siblingsWidth += cols[i].width || 0
                    }
                    const budget = Math.floor(containerWidth * AUTOFIT_FROZEN_SHARE) - siblingsWidth
                    if (budget > (col.minWidth || AUTOFIT_MIN)) width = Math.min(width, budget)
                }
            }

            if (width === col.width) return

            // Two separate "spread the columns across the viewport" passes used
            // to wipe out the width we just measured, which is why the
            // double-click looked like it did nothing at all:
            //   * forceFitColumns pins the grid's autosizeColsMode to
            //     LegacyForceFit, and turning forceFitColumns off does NOT
            //     reset that mode, so resizeCanvas() kept re-fitting;
            //   * SlickResizer.resizeGrid() calls grid.autosizeColumns()
            //     whenever enableAutoSizeColumns is set — which it is — and
            //     that redistributes every width too.
            // The user has taken manual control of the widths now, so switch
            // both passes off before applying the new width.
            const gridOpts = grid.getOptions()
            if (gridOpts.forceFitColumns || gridOpts.enableAutoSizeColumns || gridOpts.autosizeColsMode !== 'NOA') {
                grid.setOptions({
                    forceFitColumns: false,
                    enableAutoSizeColumns: false,
                    autosizeColsMode: 'NOA' // GridAutosizeColsMode.None
                })
            }

            col.width = width
            grid.setColumns(cols)
            CreateAddlHeaderRow(grid, options, cols)
            // deliberately no slickResize() here — a width change can't change
            // the grid's height, and resizeGrid() is one of the passes above.
        }

        // The library event and the container listener below can both fire for
        // the same double-click; measuring twice is wasted work, so collapse
        // them into one run.
        let lastAutoFitAt = -1e9
        const requestAutoFit = (colIdx) => {
            const now = (window.performance && performance.now) ? performance.now() : 0
            if (now - lastAutoFitAt < 400) return
            lastAutoFitAt = now
            autoFitColumn(colIdx)
        }

        grid.onColumnsResizeDblClick.subscribe((e, args) => {
            const idx = grid.getColumns().findIndex(c => String(c.id) === String(args.triggeredByColumn))
            if (idx >= 0) requestAutoFit(idx)
        })

        // SlickGrid's own onColumnsResizeDblClick only fires when both clicks
        // land on the exact same handle element, but the handles are destroyed
        // and rebuilt on every column resize — so the second click often hits a
        // brand new node and the browser reports the double-click against the
        // header instead. Listen on the grid container as well (it outlives
        // every re-render) and accept either the handle itself or a click
        // inside the handle's strip at the header's right edge. Anything
        // further into the header is left alone so ordinary click-to-sort keeps
        // working.
        const autoFitTargetIndex = (ev) => {
            if (!ev.target || !ev.target.closest) return -1
            let headerEl = ev.target.closest('.slick-resizable-handle')?.parentElement
            if (!headerEl) {
                const candidate = ev.target.closest('.slick-header-column')
                // column-group cells in the pre-header panel reuse the same
                // class but carry no dataset id — they aren't resizable
                if (candidate && candidate.dataset && candidate.dataset.id !== undefined) {
                    const rect = candidate.getBoundingClientRect()
                    if (ev.clientX >= rect.right - AUTOFIT_EDGE_ZONE) headerEl = candidate
                }
            }
            const colId = headerEl?.dataset?.id
            if (colId === undefined) return -1
            return grid.getColumns().findIndex(c => String(c.id) === String(colId))
        }

        if (this.autoFitDblClickHandler) {
            this.elementTarget.removeEventListener('dblclick', this.autoFitDblClickHandler)
        }
        this.autoFitDblClickHandler = (ev) => {
            const idx = autoFitTargetIndex(ev)
            if (idx < 0) return
            ev.preventDefault()
            requestAutoFit(idx)
        }
        this.elementTarget.addEventListener('dblclick', this.autoFitDblClickHandler)

        grid.onKeyDown.subscribe((e, args) => {
            const row = args.row;
            const cell = args.cell;
            const dataLength = grid.getDataLength();
            const isCtrlCmd = e.ctrlKey || e.metaKey;
            if (grid.getEditorLock().isActive()) {
                return;
            }
            if (isCtrlCmd && e.key === "ArrowDown") {
                if (dataLength > 0) {
                    const lastRow = dataLength - 1;
                    grid.gotoCell(lastRow, cell, true);
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
            else if (isCtrlCmd && e.key === "ArrowUp") {
                if (dataLength > 0) {
                    grid.gotoCell(0, cell, true);
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
            else if (isCtrlCmd && e.key === "ArrowRight") {
                const lastCol = grid.getColumns().length - 1;
                grid.gotoCell(row, lastCol, true);
                e.preventDefault();
                e.stopPropagation();
            }
            else if (isCtrlCmd && e.key === "ArrowLeft") {
                grid.gotoCell(row, 0, true);
                e.preventDefault();
                e.stopPropagation();
            }
            else if (e.key === 'Enter' && cell === 0 && grid.getDataItem(row) && grid.getDataItem(row).tree_link !== undefined) {
                checkItemClick(args, grid.getCellNode(row, cell).getElementsByClassName('toggle')[0]);

            }
        });

        // Cache the frozen-left panes and only toggle the shadow class on the
        // boundary transition (scrolled-from-zero vs not). querySelectorAll +
        // classList ops on every onScroll tick were causing visible jank on
        // horizontal scroll.
        let cachedLeftPanes = null
        let shadowOn = false
        grid.onScroll.subscribe((e, args) => {
            if (!cachedLeftPanes) {
                cachedLeftPanes = this.element.querySelectorAll('.slick-pane-left.frozen')
            }
            const wantShadow = args.scrollLeft > 0
            if (wantShadow === shadowOn) return
            shadowOn = wantShadow
            cachedLeftPanes.forEach(p => p.classList.toggle('shadow-visible', wantShadow))
        })

        this.grid = grid
        this.options = options

        // SlickGrid's handleClick calls getTextSelection()+setTextSelection() to preserve
        // text selection for Firefox. When a text selection exists, setTextSelection() calls
        // window.getSelection().addRange() which fires a selectionchange event. CodeMirror's
        // global selectionchange listener then crashes when view.root is unavailable.
        // Returning null here skips the addRange() call entirely.
        this.grid.getTextSelection = () => null

        // Re-format currency cells when the number-scale dropdown changes the
        // card's data-report-scale. The cells format client-side in the column
        // formatter now (no per-cell Stimulus controller), so re-rendering the
        // visible rows re-applies the new scale.
        this.cardScaleObserver = new MutationObserver(() => {
            grid.invalidateAllRows()
            grid.render()
        })
        this.cardScaleObserver.observe(card, {attributes: true, attributeFilter: ['data-report-scale']})

        slickResize()
        this.setupStickyHeaders()
    }



    setupStickyHeaders() {
        // Store references to the header elements
        this.headerLeft = this.elementTarget.querySelector('.slick-pane-header.slick-pane-left');
        this.headerRight = this.elementTarget.querySelector('.slick-pane-header.slick-pane-right');

        // Store the grid container reference
        this.gridContainer = this.elementTarget;

        // Create placeholder elements
        this.leftPlaceholder = document.createElement('div');
        this.rightPlaceholder = document.createElement('div');

        // Flag to track if headers are currently sticky
        this.isSticky = false;

        // Store original header styles
        this.originalStyles = {
            left: null,
            right: null
        };

        // Bind event handlers
        this.handleScroll = this.handleScroll.bind(this);

        // rAF-coalesce the scroll handler — handleScroll does two
        // getBoundingClientRect calls (forced layout) per invocation, and the
        // previous 10ms throttle was firing it ~100×/sec, which is what made
        // page scroll choppy when the grid has a sticky header.
        let scrollPending = false
        const onWindowScroll = () => {
            if (scrollPending) return
            scrollPending = true
            requestAnimationFrame(() => {
                scrollPending = false
                this.handleScroll()
            })
        }
        window.addEventListener('scroll', onWindowScroll, {passive: true});

        // Initial calculation after a short delay to ensure grid is fully rendered
        setTimeout(() => this.initializeHeaderTracking(), 300);
    }
    initializeHeaderTracking() {
        if (!this.headerLeft || !this.headerRight) return;

        // Save original positions and styles
        const leftRect = this.headerLeft.getBoundingClientRect();
        const rightRect = this.headerRight.getBoundingClientRect();

        // Store original styles
        this.originalStyles = {
            left: {
                position: this.headerLeft.style.position,
                top: this.headerLeft.style.top,
                width: this.headerLeft.style.width,
                zIndex: this.headerLeft.style.zIndex
            },
            right: {
                position: this.headerRight.style.position,
                top: this.headerRight.style.top,
                width: this.headerRight.style.width,
                zIndex: this.headerRight.style.zIndex,
                left: this.headerRight.style.left
            }
        };

        // Set up placeholder dimensions
        this.leftPlaceholder.style.height = `${leftRect.height}px`;
        this.leftPlaceholder.style.width = `${leftRect.width}px`;
        this.leftPlaceholder.style.display = 'none';

        this.rightPlaceholder.style.height = `${rightRect.height}px`;
        this.rightPlaceholder.style.width = `${rightRect.width}px`;
        this.rightPlaceholder.style.display = 'none';

        // Append placeholders to the DOM
        if (this.headerLeft.parentNode) {
            this.headerLeft.parentNode.insertBefore(this.leftPlaceholder, this.headerLeft);
        }

        if (this.headerRight.parentNode) {
            this.headerRight.parentNode.insertBefore(this.rightPlaceholder, this.headerRight);
        }

        // Initial position check
        this.handleScroll();
    }

    throttle(func, limit) {
        let lastFunc;
        let lastRan;
        return function() {
            const context = this;
            const args = arguments;
            if (!lastRan) {
                func.apply(context, args);
                lastRan = Date.now();
            } else {
                clearTimeout(lastFunc);
                lastFunc = setTimeout(function() {
                    if ((Date.now() - lastRan) >= limit) {
                        func.apply(context, args);
                        lastRan = Date.now();
                    }
                }, limit - (Date.now() - lastRan));
            }
        };
    }

    handleScroll() {
        if (!this.headerLeft || !this.headerRight || !this.gridContainer) return;

        // Get current grid position
        const gridRect = this.gridContainer.getBoundingClientRect();
        const headerRect = this.headerLeft.getBoundingClientRect();
        const scrollY = window.scrollY;

        // Grid is in view and header should be sticky
        const headerHeight = headerRect.height;
        const shouldBeSticky = gridRect.top < 0 && gridRect.bottom > headerHeight;

        if (shouldBeSticky && !this.isSticky) {
            this.makeHeadersSticky();
        } else if (!shouldBeSticky && this.isSticky) {
            this.unstickHeaders();
        }

        // When sticky, we need to handle the case where we're scrolling past the grid
        if (this.isSticky) {
            // If we've scrolled past the bottom of the grid minus header height,
            // we need to position the headers absolutely rather than fixed
            if (gridRect.bottom < headerHeight) {
                this.headerLeft.style.position = 'absolute';
                this.headerLeft.style.top = `${gridRect.height - headerHeight}px`;

                this.headerRight.style.position = 'absolute';
                this.headerRight.style.top = `${gridRect.height - headerHeight}px`;
            } else {
                // Otherwise keep them fixed at top: 0
                this.headerLeft.style.position = 'fixed';
                this.headerLeft.style.top = '0';

                this.headerRight.style.position = 'fixed';
                this.headerRight.style.top = '0';
            }
        }
    }

    makeHeadersSticky() {
        if (this.isSticky) return;
        this.isSticky = true;

        // Show placeholders
        this.leftPlaceholder.style.display = 'block';
        this.rightPlaceholder.style.display = 'block';

        // Save current positions and sizes
        const leftRect = this.headerLeft.getBoundingClientRect();
        const rightRect = this.headerRight.getBoundingClientRect();

        // Make headers fixed
        this.headerLeft.style.position = 'fixed';
        this.headerLeft.style.top = '0';
        this.headerLeft.style.zIndex = '1000';
        this.headerLeft.style.width = `${leftRect.width}px`;
        this.headerLeft.style.backgroundColor = '#eef2ff';

        this.headerRight.style.position = 'fixed';
        this.headerRight.style.top = '0';
        this.headerRight.style.zIndex = '1000';
        this.headerRight.style.left = `${rightRect.left}px`;
        this.headerRight.style.width = `${rightRect.width}px`;
        this.headerRight.style.backgroundColor = '#eef2ff';

        // Add shadow for visual indication
        this.headerLeft.style.boxShadow = '0 4px 10px -3px rgba(79,70,229,0.18)';
        this.headerRight.style.boxShadow = '0 4px 10px -3px rgba(79,70,229,0.18)';
    }

    unstickHeaders() {
        if (!this.isSticky) return;
        this.isSticky = false;

        // Hide placeholders
        this.leftPlaceholder.style.display = 'none';
        this.rightPlaceholder.style.display = 'none';

        // Restore original styles
        if (this.originalStyles.left) {
            this.headerLeft.style.position = this.originalStyles.left.position;
            this.headerLeft.style.top = this.originalStyles.left.top;
            this.headerLeft.style.zIndex = this.originalStyles.left.zIndex;
            this.headerLeft.style.width = this.originalStyles.left.width;
        }

        if (this.originalStyles.right) {
            this.headerRight.style.position = this.originalStyles.right.position;
            this.headerRight.style.top = this.originalStyles.right.top;
            this.headerRight.style.zIndex = this.originalStyles.right.zIndex;
            this.headerRight.style.left = this.originalStyles.right.left;
            this.headerRight.style.width = this.originalStyles.right.width;
        }

        // Remove shadows
        this.headerLeft.style.boxShadow = '';
        this.headerRight.style.boxShadow = '';

        // Remove background color
        this.headerLeft.style.backgroundColor = '';
        this.headerRight.style.backgroundColor = '';
    }

    disconnect() {
        // Clean up event listeners
        window.removeEventListener('scroll', this.handleScroll);

        // Stop watching the card's number-scale attribute
        if (this.scaleObserver) {
            this.scaleObserver.disconnect();
        }
        if (this.cardScaleObserver) {
            this.cardScaleObserver.disconnect();
        }

        // If headers are sticky, unstick them
        if (this.isSticky) {
            this.unstickHeaders();
        }

        // Remove placeholders
        if (this.leftPlaceholder && this.leftPlaceholder.parentNode) {
            this.leftPlaceholder.parentNode.removeChild(this.leftPlaceholder);
        }

        if (this.rightPlaceholder && this.rightPlaceholder.parentNode) {
            this.rightPlaceholder.parentNode.removeChild(this.rightPlaceholder);
        }

        // Call the original disconnect
        this.grid.destroy();
    }

    reattachStickyHeaders() {
        // Reset sticky state
        if (this.isSticky) {
            this.unstickHeaders();
        }

        // Get fresh references to header elements
        this.headerLeft = this.elementTarget.querySelector('.slick-pane-header.slick-pane-left');
        this.headerRight = this.elementTarget.querySelector('.slick-pane-header.slick-pane-right');

        // Re-initialize if needed
        if (this.headerLeft && this.headerRight) {
            // Update placeholder dimensions
            const leftRect = this.headerLeft.getBoundingClientRect();
            const rightRect = this.headerRight.getBoundingClientRect();

            this.leftPlaceholder.style.height = `${leftRect.height}px`;
            this.leftPlaceholder.style.width = `${leftRect.width}px`;

            this.rightPlaceholder.style.height = `${rightRect.height}px`;
            this.rightPlaceholder.style.width = `${rightRect.width}px`;

            // Save original styles again
            this.originalStyles = {
                left: {
                    position: this.headerLeft.style.position,
                    top: this.headerLeft.style.top,
                    width: this.headerLeft.style.width,
                    zIndex: this.headerLeft.style.zIndex
                },
                right: {
                    position: this.headerRight.style.position,
                    top: this.headerRight.style.top,
                    width: this.headerRight.style.width,
                    zIndex: this.headerRight.style.zIndex,
                    left: this.headerRight.style.left
                }
            };
        }
    }

    CreateAddlHeaderRow(grid, options, columns) {
        // when it's a frozen grid, we need to render both side (left/right)
        if (options.frozenColumn >= 0) {
            // Add column groups to left panel
            let preHeaderPanelElm = grid.getPreHeaderPanelLeft();
            renderHeaderGroups(preHeaderPanelElm, 0, options.frozenColumn + 1, columns.length);

            // Add column groups to right panel
            preHeaderPanelElm = grid.getPreHeaderPanelRight();
            renderHeaderGroups(preHeaderPanelElm, options.frozenColumn + 1, columns.length);
        } else {
            // when it's a regular grid (without frozen rows), after clearning frozen columns, we re-render everything on the left
            let preHeaderPanelElm = grid.getPreHeaderPanelLeft();
            renderHeaderGroups(preHeaderPanelElm, 0, columns.length);
        }


        function renderHeaderGroups(preHeaderPanel, start, end) {
            preHeaderPanel.innerHTML = '';
            preHeaderPanel.classList.add("slick-header-columns");
            preHeaderPanel.style.left = '-1000px';
            preHeaderPanel.style.width = `${grid.getHeadersWidth()}px`;
            preHeaderPanel.parentElement.classList.add("slick-header");

            let headerColumnWidthDiff = grid.getHeaderColumnWidthDiff();
            let m, header, lastColumnGroup = '', widthTotal = 0;
            const columns = grid.getColumns();

            for (let i = start; i < end; i++) {
                m = columns[i];
                if (m) {
                    if (lastColumnGroup === m.columnGroup && i > start) {
                        widthTotal += m.width;
                        header.style.width = `${widthTotal - headerColumnWidthDiff}px`;
                    } else {
                        widthTotal = m.width;
                        header = document.createElement('div');
                        header.className = 'ui-state-default slick-header-column text-center';
                        header.innerHTML = `<span class="slick-column-name">${(m.columnGroup || '')}</span>`;
                        header.style.width = `${m.width - headerColumnWidthDiff}px`;
                        preHeaderPanel.appendChild(header);
                    }
                    lastColumnGroup = m.columnGroup;
                }
            }

        }

    }

    // Raw, sign-correct numeric value of a cell. `_sort_value` is built by
    // TabulatorHelper to equal the cell's `data-currency-amount-value`
    // (unscaled, expense-sign applied), so it is the canonical figure to sum.
    cellNumber(item, field) {
        const raw = item[`${field}_sort_value`];
        if (raw === undefined || raw === null || raw === '') return null;
        const num = typeof raw === 'number' ? raw : parseFloat(raw);
        return isFinite(num) ? num : null;
    }

    // Mirrors currency_controller's scale/comma/precision logic so the summed
    // figure matches the on-screen cells, including the live "₹ Lakhs" scale
    // toggle (written as data-report-scale on the card) and per-cell overrides.
    SCALE_DIVISORS = { units: 1, thousands: 1000, lakhs: 100000, millions: 1000000, crores: 10000000 }

    metaContent(name) {
        return document.querySelector(`meta[name="${name}"]`)?.content
    }

    isBlankAttr(v) {
        return v === null || v === undefined || v === '' || v === 'null' || v === 'undefined'
    }

    reportScaleOverride() {
        const scale = this.card && this.card.getAttribute('data-report-scale')
        return (scale && scale !== '' && scale !== 'default') ? scale : null
    }

    resolveScale(cellSpan) {
        let scale = this.reportScaleOverride()
        if (this.isBlankAttr(scale)) scale = cellSpan && cellSpan.getAttribute('data-currency-number-scale-value')
        if (this.isBlankAttr(scale)) scale = this.metaContent('userRoundPreference') || 'units'
        return scale
    }

    resolveCommaLocale(cellSpan) {
        let style = cellSpan && cellSpan.getAttribute('data-currency-comma-style-value')
        if (this.isBlankAttr(style)) style = this.metaContent('userCommaStyle') || 'indian'
        return style === 'western' ? 'en-US' : 'en-IN'
    }

    resolvePrecision(cellSpan, scale) {
        // A scale override forces precision to come from the user default.
        let p = this.reportScaleOverride() ? '' : (cellSpan && cellSpan.getAttribute('data-currency-precision-value'))
        if (this.isBlankAttr(p)) p = this.metaContent('userPrecision') || ''
        if (p === '') return scale === 'units' ? 0 : 2
        return parseInt(p)
    }

    formatSum(amount, cellSpan) {
        const scale = this.resolveScale(cellSpan)
        const locale = this.resolveCommaLocale(cellSpan)
        const precision = this.resolvePrecision(cellSpan, scale)
        const divisor = this.SCALE_DIVISORS[scale] || 1
        const scaled = parseFloat((Math.abs(amount) / divisor).toFixed(precision))
        const formatted = new Intl.NumberFormat(locale, {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision
        }).format(scaled)
        return amount < 0 ? `(${formatted})` : formatted
    }

    ensureStatusBar() {
        if (this.statusBar) return this.statusBar;

        // The report card carries the live data-report-scale used for currency
        // formatting; fall back to the controller element when there is no card.
        this.card = this.element.querySelector('.lz-report-card') || this.element;

        const bar = document.createElement('div');
        bar.style.display = 'none';
        bar.className = 'slick-selection-stats items-center justify-end gap-2 ' +
            'px-3 py-1.5 bg-indigo-50 border-t border-indigo-100 rounded-b-xl';
        bar.innerHTML =
            '<span class="slick-stat-count inline-flex items-center gap-1 rounded-md bg-white ' +
            'px-2 py-0.5 text-xs font-medium text-gray-600 border border-indigo-100"></span>' +
            '<span class="slick-stat-sum inline-flex items-center gap-1 rounded-md bg-indigo-600 ' +
            'px-2 py-0.5 text-xs font-semibold text-white shadow-sm"></span>';

        this.card.appendChild(bar);

        // The scale dropdown rewrites data-report-scale on the card without
        // touching the selection, so recompute the sum when it changes while a
        // selection is active.
        this.scaleObserver = new MutationObserver(() => {
            if (this.lastRanges) {
                this.updateSelectionStats(this.lastRanges, this.grid, this.grid.getData());
            }
        });
        this.scaleObserver.observe(this.card, {
            attributes: true,
            attributeFilter: ['data-report-scale']
        });

        this.statusBar = bar;
        return bar;
    }

    updateSelectionStats(ranges, grid, dataView) {
        const bar = this.ensureStatusBar();
        this.lastRanges = ranges;

        if (!ranges || ranges.length === 0) {
            bar.style.display = 'none';
            return;
        }

        let cellCount = 0;
        let numericCount = 0;
        let sum = 0;
        let cellSpan = null; // a representative currency span for formatting attrs
        const columns = grid.getColumns();

        for (const range of ranges) {
            for (let r = range.fromRow; r <= range.toRow; r++) {
                const item = dataView.getItem(r);
                if (!item) continue;
                for (let c = range.fromCell; c <= range.toCell; c++) {
                    const col = columns[c];
                    if (!col) continue;
                    cellCount++;
                    const num = this.cellNumber(item, col.field);
                    if (num !== null) {
                        numericCount++;
                        sum += num;
                        if (!cellSpan) {
                            const node = grid.getCellNode(r, c);
                            // Currency cells are now formatter-built `.currency`
                            // spans (no data-controller); still carry the
                            // data-currency-* attrs the sum reads.
                            cellSpan = node && node.querySelector('.currency');
                        }
                    }
                }
            }
        }

        // Nothing meaningful to show for a single cell.
        if (cellCount <= 1) {
            bar.style.display = 'none';
            return;
        }

        bar.querySelector('.slick-stat-count').textContent = `Count ${cellCount}`;

        const sumEl = bar.querySelector('.slick-stat-sum');
        if (numericCount > 0) {
            sumEl.textContent = `Sum ${this.formatSum(sum, cellSpan)}`;
            sumEl.style.display = '';
        } else {
            sumEl.style.display = 'none';
        }

        bar.style.display = 'flex';
    }

    heyCopy(randStr) {
        let table = document.createElement('table');

        table.id = randStr;

        this.ogHeadersValue.forEach(rows => {
            let row = table.insertRow();
            rows.forEach(header => {
                if (header.value !== undefined) {
                    let cell = row.insertCell();
                    cell.textContent = header.value || '';
                    if (_.has(header, 'rowspan')) {
                        cell.rowSpan = header.rowspan;
                    }
                    if (_.has(header, 'colspan')) {
                        cell.colSpan = header.colspan;
                    }
                }
            })
        })

        this.grid.getData().items.forEach(rowData => {
            let row = table.insertRow();
            this.grid.getColumns().forEach(col => {
                let cell = row.insertCell();
                let vals = [rowData[`${col.field}_copy_value`], rowData[`${col.field}_sort_value`], rowData[col.field], '']
                cell.innerHTML = _.find(vals, (value) => value !== null && value !== undefined);
            });
        });

        table.classList.add('hidden')
        document.body.appendChild(table);


    }

    copy(e) {
        let ele = undefined
        if (e.target.classList.contains('lz-copy-icon')) {
            ele = e.target
        } else {
            ele = e.target.children[0]
        }
        ele.classList.remove('lz-copy-icon')
        ele.classList.add('lz-loader-icon')
        let randStr = (Math.random() + 1).toString(36).substring(7);
        this.heyCopy(randStr)

        const table = document.getElementById(randStr)
        const html = table.outerHTML.replace(/href=/g, '')
        // Tab-separated fallback for editors that only read text/plain. Strip
        // tabs/newlines inside a cell so they don't break the row/column grid.
        const tsv = Array.from(table.rows).map(row =>
            Array.from(row.cells).map(c => (c.textContent || '').replace(/[\t\r\n]+/g, ' ')).join('\t')
        ).join('\n')
        table.remove()

        // Put BOTH a text/html flavour (so LibreOffice Calc, Google Sheets,
        // Excel and Word parse it into cells) and a text/plain TSV flavour
        // (plain editors) on the clipboard. The old writeText() only wrote
        // text/plain HTML markup, which only Excel heuristically parsed.
        this.writeClipboard(html, tsv)
            .then(() => {
                ele.classList.remove('lz-loader-icon')
                ele.classList.add('lz-check-icon')
                _.delay(function () {
                    ele.classList.remove('lz-check-icon')
                    ele.classList.add('lz-copy-icon')
                }, 3000)
            })
            .catch(error => {
                console.error('Error copying table data to clipboard: ', error)
                ele.classList.remove('lz-loader-icon')
                ele.classList.add('lz-copy-icon')
            })
    }

    // Writes rich (text/html) + plain (text/plain) clipboard flavours so the
    // table pastes as cells in every spreadsheet, not just Excel. Falls back to
    // writeText for browsers without ClipboardItem.
    writeClipboard(html, text) {
        if (navigator.clipboard && window.ClipboardItem) {
            const item = new ClipboardItem({
                'text/html': new Blob([html], {type: 'text/html'}),
                'text/plain': new Blob([text], {type: 'text/plain'})
            })
            return navigator.clipboard.write([item])
        }
        return navigator.clipboard.writeText(text)
    }



    collapseAll() {
        let dataView = this.grid.getData()
        dataView.beginUpdate()
        dataView.items.forEach(rowData => {
            if (_.has(rowData, '_collapsed')) {
                rowData._collapsed = true
                dataView.updateItem(rowData.id, rowData)
            }
        })
        dataView.endUpdate()
    }

    moveRight() {
        this.grid.navigateNext()
    }

    startScrollLeft(speed) {
        if (this.scrollInterval) return; // Avoid multiple intervals

        this.scrollInterval = setInterval(() => {
            this.moveLeft();
        }, 100); // Adjust interval for smoother/faster scrolling
    }

    stopScrollLeft() {
        if (this.scrollInterval) {
            clearInterval(this.scrollInterval);
            this.scrollInterval = null;
        }
    }

    startScrollRight(speed) {
        if (this.scrollInterval) return; // Avoid multiple intervals

        this.scrollInterval = setInterval(() => {
            this.moveRight();
        }, 100); // Adjust interval for smoother/faster scrolling
    }

    stopScrollRight() {
        if (this.scrollInterval) {
            clearInterval(this.scrollInterval);
            this.scrollInterval = null;
        }
    }


    moveLeft() {
        this.grid.navigatePrev()
    }

    toggleColumn(event) {
        _.forEach(this.slickHeaders, function (obj) {
            if (obj['dValue'] === event.target.dataset['gridColValue'] || obj['columnGroupName'] === event.target.dataset['gridColValue']) {
                obj.hidden = !event.target.checked
            }
        })
        this.grid.setColumns(_.filter(this.slickHeaders, ['hidden', false]))
        this.CreateAddlHeaderRow(this.grid, this.options, this.grid.columns)
    }

    toggleAllColumn(event) {

    }

}
